<template>
  <div class="feedback-wrapper">

    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h1>Ratings & Reviews</h1>
        <p>Manage user feedback, ratings and reviews.</p>
      </div>

      <button class="primary-btn" @click="openAddModal">
        <i class="bx bx-plus"></i>
        Add Review
      </button>
    </div>

    <!-- Stats -->
    <div class="stats-grid">

      <!-- Total Reviews -->
      <div class="stat-card">
        <div class="stat-icon blue">
          <i class="bx bx-message-square-detail"></i>
        </div>

        <div>
          <span>Total Reviews</span>
          <strong>{{ totalFeedbacks }}</strong>
        </div>
      </div>

      <!-- Average Rating -->
      <div class="stat-card">
        <div class="stat-icon green">
          <i class="bx bx-star"></i>
        </div>

        <div>
          <span>Average Rating</span>
          <strong>{{ averageRating }}</strong>
        </div>
      </div>

      <!-- Video Reviews -->
      <div class="stat-card">
        <div class="stat-icon orange">
          <i class="bx bx-video"></i>
        </div>

        <div>
          <span>Video Reviews</span>
          <strong>{{ videoReviews }}</strong>
        </div>
      </div>

      <!-- Course Reviews -->
      <div class="stat-card">
        <div class="stat-icon purple">
          <i class="bx bx-book"></i>
        </div>

        <div>
          <span>Course Reviews</span>
          <strong>{{ courseReviews }}</strong>
        </div>
      </div>

    </div>

    <!-- Rating Summary -->
    <div class="rating-summary-card">

      <div class="rating-overall">
        <strong>{{ averageRating }}</strong>

        <div class="stars large">
          <i
            v-for="star in 5"
            :key="star"
            class="bx"
            :class="
              star <= Math.round(Number(averageRating))
                ? 'bxs-star'
                : 'bx-star'
            "
          ></i>
        </div>

        <span>
          Based on {{ totalFeedbacks }} reviews
        </span>
      </div>

      <div class="rating-bars">

        <div
          v-for="rating in [5, 4, 3, 2, 1, 0]"
          :key="rating"
          class="rating-row"
        >
          <span>{{ rating }}</span>

          <i class="bx bxs-star"></i>

          <div class="rating-progress">
            <div
              class="rating-progress-fill"
              :style="{
                width: ratingPercentage(rating) + '%'
              }"
            ></div>
          </div>

          <small>
            {{ ratingCount(rating) }}
          </small>
        </div>

      </div>
    </div>

    <!-- Filters -->
    <div class="content-card">

      <div class="toolbar">

        <div class="search-box">
          <i class="bx bx-search"></i>

          <input
            v-model="search"
            type="text"
            placeholder="Search review, user or content..."
          />
        </div>

        <select
          v-model="ratingFilter"
          class="filter-select"
        >
          <option value="">All Ratings</option>
          <option value="5">5 Stars</option>
          <option value="4">4 Stars</option>
          <option value="3">3 Stars</option>
          <option value="2">2 Stars</option>
          <option value="1">1 Star</option>
        </select>

        <select
          v-model="typeFilter"
          class="filter-select"
        >
          <option value="">All Types</option>
          <option value="Course">Course</option>
          <option value="Creator">Creator</option>
          <option value="General">General</option>
        </select>

        <select
          v-model="mediaFilter"
          class="filter-select"
        >
          <option value="">All Reviews</option>
          <option value="text">Text Reviews</option>
          <option value="image">Image Reviews</option>
          <option value="video">Video Reviews</option>
        </select>

        <button
          class="reset-btn"
          @click="resetFilters"
        >
          <i class="bx bx-reset"></i>
          Reset
        </button>

      </div>

      <!-- Table -->
      <div class="table-wrapper">

        <table>

          <thead>
            <tr>
              <th>Reviewer</th>
              <th>Review</th>
              <th>Rating</th>
              <th>Type</th>
              <th>Related To</th>
              <th>Media</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>

            <!-- Loading -->
            <tr v-if="loading">
              <td colspan="8">

                <div class="empty-state">
                  <i class="bx bx-loader-alt bx-spin"></i>
                  <h3>Loading reviews...</h3>
                  <p>Please wait.</p>
                </div>

              </td>
            </tr>

            <!-- Empty -->
            <tr
              v-else-if="feedbacks.length === 0"
            >
              <td colspan="8">

                <div class="empty-state">
                  <i class="bx bx-message-square-x"></i>

                  <h3>No reviews found</h3>

                  <p>
                    Try changing your search or filters.
                  </p>
                </div>

              </td>
            </tr>

            <!-- Feedback -->
            <tr
              v-else
              v-for="feedback in feedbacks"
              :key="feedback.feed_id"
            >

              <!-- Reviewer -->
              <td>

                <div class="reviewer-info">

                  <div class="avatar">

                    <img
                      v-if="feedback.user_image"
                      :src="feedback.user_image"
                      :alt="feedback.user_name"
                    />

                    <span v-else>
                      {{ getInitials(feedback.user_name) }}
                    </span>

                  </div>

                  <div>
                    <strong>
                      {{ feedback.user_name || "Unknown User" }}
                    </strong>

                    <small>
                      User #{{ feedback.feed_by }}
                    </small>
                  </div>

                </div>

              </td>

              <!-- Review -->
              <td>

                <div class="review-text">

                  <strong>
                    {{ feedback.feed_text || "—" }}
                  </strong>

                  <small
                    v-if="feedback.feed_meta_text"
                  >
                    {{ feedback.feed_meta_text }}
                  </small>

                </div>

              </td>

              <!-- Rating -->
              <td>

                <div class="rating-cell">

                  <div class="stars">

                    <i
                      v-for="star in 5"
                      :key="star"
                      class="bx"
                      :class="
                        star <=
                        Math.round(
                          Number(feedback.feed_rating)
                        )
                          ? 'bxs-star'
                          : 'bx-star'
                      "
                    ></i>

                  </div>

                  <span>
                    {{
                      Number(
                        feedback.feed_rating || 0
                      ).toFixed(1)
                    }}
                  </span>

                </div>

              </td>

             
              <!-- Related -->
              <td>

                <div class="related-info">

                  <strong>
                    {{ getRelatedName(feedback) }}
                  </strong>

                  <small
                    v-if="
                      feedback.feed_parent_type &&
                      feedback.feed_parent !== null &&
                      feedback.feed_parent !== undefined
                    "
                  >
                    ID #{{ feedback.feed_parent }}
                  </small>

                </div>

              </td>

              <!-- Media -->
              <td>

                <div class="media-list">

                  <span
                    v-if="feedback.feed_video"
                    title="Video"
                  >
                    <i class="bx bx-video"></i>
                  </span>

                  <span
                    v-if="feedback.feed_img"
                    title="Image"
                  >
                    <i class="bx bx-image"></i>
                  </span>

                  <span
                    v-if="
                      !feedback.feed_video &&
                      !feedback.feed_img
                    "
                    class="no-media"
                  >
                    Text
                  </span>

                </div>

              </td>

              <!-- Date -->
              <td>

                <span class="date-text">
                  {{ formatDate(feedback.feed_datetime) }}
                </span>

              </td>

              <!-- Actions -->
              <td>

                <div class="actions">

                  <button
                    class="icon-btn view"
                    title="View"
                    @click="viewFeedback(feedback)"
                  >
                    <i class="bx bx-show"></i>
                  </button>

                  <button
                    class="icon-btn edit"
                    title="Edit"
                    @click="editFeedback(feedback)"
                  >
                    <i class="bx bx-edit-alt"></i>
                  </button>

                  <button
                    class="icon-btn delete"
                    title="Delete"
                    @click="confirmDelete(feedback)"
                  >
                    <i class="bx bx-trash"></i>
                  </button>

                </div>

              </td>

            </tr>

          </tbody>

        </table>

      </div>

      <!-- Pagination -->
      <div
        v-if="totalFeedbacks > 0"
        class="pagination"
      >

        <span>
          Showing
          {{ startItem }}–{{ endItem }}
          of
          {{ totalFeedbacks }}
        </span>

        <div class="pagination-buttons">

          <button
            :disabled="currentPage === 1"
            @click="previousPage"
          >
            <i class="bx bx-chevron-left"></i>
          </button>

          <button
            v-for="pageNumber in totalPagesCount"
            :key="pageNumber"
            :class="{
              active: currentPage === pageNumber
            }"
            @click="goToPage(pageNumber)"
          >
            {{ pageNumber }}
          </button>

          <button
            :disabled="
              currentPage === totalPagesCount
            "
            @click="nextPage"
          >
            <i class="bx bx-chevron-right"></i>
          </button>

        </div>

      </div>

    </div>

    <!-- Add / Edit Modal -->
    <div
      v-if="showFormModal"
      class="modal-overlay"
      @click.self="closeFormModal"
    >

      <div class="modal">

        <div class="modal-header">

          <div>

            <h2>
              {{
                isEditing
                  ? "Edit Review"
                  : "Add Review"
              }}
            </h2>

            <p>
              {{
                isEditing
                  ? "Update feedback and rating information."
                  : "Create a new user review."
              }}
            </p>

          </div>

          <button
            class="close-btn"
            @click="closeFormModal"
          >
            <i class="bx bx-x"></i>
          </button>

        </div>

        <div class="modal-body">

          <div class="form-grid">

            <!-- User -->
            <div class="form-group">
              <label>User <span>*</span></label>

              <select v-model.number="form.feed_by">

                <option :value="null">
                  Select User
                </option>

                <option
                  v-for="user in users"
                  :key="user.user_id"
                  :value="user.user_id"
                >
                  {{
                    user.name ||
                    user.email ||
                    `User #${user.user_id}`
                  }}
                </option>

              </select>
            </div>

            <!-- Parent Type -->
            <div class="form-group">

              <label>
                Parent Type <span>*</span>
              </label>

              <select
                v-model="form.feed_parent_type"
              >
                <option value="">
                  Select Parent Type
                </option>

                <option value="Home">
                  Home
                </option>

                <option value="Course">
                  Course
                </option>

                <option value="Creator">
                  Creator
                </option>

                <option value="Blog">
                  Blog
                </option>
              </select>

            </div>

            <!-- Course -->
            <div
              v-if="
                form.feed_parent_type === 'Course'
              "
              class="form-group"
            >

              <label>
                Course <span>*</span>
              </label>

              <select
                v-model.number="form.feed_parent"
              >

                <option :value="null">
                  Select Course
                </option>

                <option
                  v-for="course in courses"
                  :key="course.course_id"
                  :value="course.course_id"
                >
                  {{ course.title }}
                </option>

              </select>

            </div>

            <!-- Creator -->
            <div
              v-if="
                form.feed_parent_type === 'Creator'
              "
              class="form-group"
            >

              <label>
                Creator <span>*</span>
              </label>

              <select
                v-model.number="form.feed_parent"
              >

                <option :value="null">
                  Select Creator
                </option>

                <option
                  v-for="creator in creators"
                  :key="creator.creator_id"
                  :value="creator.creator_id"
                >
                  {{
                    creator.cre_username ||
                    creator.cre_buss_name ||
                    `Creator #${creator.creator_id}`
                  }}
                </option>

              </select>

            </div>

            <!-- Blog -->
            <div
              v-if="
                form.feed_parent_type === 'Blog'
              "
              class="form-group"
            >

              <label>
                Blog <span>*</span>
              </label>

              <select
                v-model.number="form.feed_parent"
              >

                <option :value="null">
                  Select Blog
                </option>

                <option
                  v-for="blog in blogs"
                  :key="blog.blog_id"
                  :value="blog.blog_id"
                >
                  {{
                    blog.blog_title ||
                    blog.title ||
                    `Blog #${blog.blog_id}`
                  }}
                </option>

              </select>

            </div>

            <!-- Home -->
            <div
              v-if="
                form.feed_parent_type === 'Home'
              "
              class="form-group"
            >

              <label>Parent ID</label>

              <input
                type="number"
                value="0"
                disabled
              />

              <small>
                Home reviews always use Parent ID 0.
              </small>

            </div>


            <!-- Rating -->
            <div class="form-group">

              <label>
                Rating <span>*</span>
              </label>

              <select
                v-model.number="form.feed_rating"
              >
                <option :value="0">
                  0.0 - No Rating
                </option>

                <option :value="5">
                  5.0 - Excellent
                </option>

                <option :value="4">
                  4.0 - Good
                </option>

                <option :value="3">
                  3.0 - Average
                </option>

                <option :value="2">
                  2.0 - Poor
                </option>

                <option :value="1">
                  1.0 - Very Poor
                </option>
              </select>

            </div>

            <!-- Review -->
            <div class="form-group full">

              <label>
                Review Text <span>*</span>
              </label>

              <textarea
                v-model="form.feed_text"
                rows="4"
                placeholder="Enter review..."
              ></textarea>

            </div>

            <!-- Meta Text -->
            <div class="form-group full">

              <label>Meta Text</label>

              <textarea
                v-model="form.feed_meta_text"
                rows="2"
                placeholder="Meta text"
              ></textarea>

            </div>

            <!-- Keywords -->
            <div class="form-group">

              <label>Keywords</label>

              <input
                v-model="form.feed_keyword"
                type="text"
                placeholder="review, course, learning"
              />

            </div>

            <!-- Image -->
            <div class="form-group">

              <label>Image URL</label>

              <input
                v-model="form.feed_img"
                type="text"
                placeholder="Image URL"
              />

            </div>

            <!-- Video -->
            <div class="form-group full">

              <label>Video URL</label>

              <input
                v-model="form.feed_video"
                type="text"
                placeholder="Video URL"
              />

            </div>

          </div>

        </div>

        <div class="modal-footer">

          <button
            class="secondary-btn"
            :disabled="saving"
            @click="closeFormModal"
          >
            Cancel
          </button>

          <button
            class="primary-btn"
            :disabled="saving"
            @click="saveFeedback"
          >

            <i
              class="bx"
              :class="
                saving
                  ? 'bx-loader-alt bx-spin'
                  : 'bx-check'
              "
            ></i>

            {{
              saving
                ? (
                    isEditing
                      ? "Updating..."
                      : "Creating..."
                  )
                : (
                    isEditing
                      ? "Update Review"
                      : "Create Review"
                  )
            }}

          </button>

        </div>

      </div>

    </div>

    <!-- View Modal -->
    <div
      v-if="showViewModal"
      class="modal-overlay"
      @click.self="showViewModal = false"
    >

      <div class="modal view-modal">

        <div class="modal-header">

          <div>
            <h2>Review Details</h2>
            <p>Complete feedback information.</p>
          </div>

          <button
            class="close-btn"
            @click="showViewModal = false"
          >
            <i class="bx bx-x"></i>
          </button>

        </div>

        <div
          v-if="selectedFeedback"
          class="modal-body"
        >

          <!-- Reviewer -->
          <div class="view-reviewer">

            <div class="large-avatar">

              <img
                v-if="selectedFeedback.user_image"
                :src="selectedFeedback.user_image"
                :alt="selectedFeedback.user_name"
              />

              <span v-else>
                {{
                  getInitials(
                    selectedFeedback.user_name
                  )
                }}
              </span>

            </div>

            <div>

              <h3>
                {{
                  selectedFeedback.user_name ||
                  "Unknown User"
                }}
              </h3>

              <p>
                User #{{ selectedFeedback.feed_by }}
              </p>

              <div class="stars">

                <i
                  v-for="star in 5"
                  :key="star"
                  class="bx"
                  :class="
                    star <=
                    Math.round(
                      Number(
                        selectedFeedback.feed_rating
                      )
                    )
                      ? 'bxs-star'
                      : 'bx-star'
                  "
                ></i>

              </div>

            </div>

          </div>

          <!-- Review -->
          <div class="detail-section">

            <h4>Review</h4>

            <div class="review-detail-box">

              <p>
                {{
                  selectedFeedback.feed_text ||
                  "No review text."
                }}
              </p>

              <small
                v-if="
                  selectedFeedback.feed_meta_text
                "
              >
                {{
                  selectedFeedback.feed_meta_text
                }}
              </small>

            </div>

          </div>

          <!-- Details -->
          <div class="detail-section">

            <h4>Review Information</h4>

            <div class="details-grid">

              <div class="detail-item">
                <span>Rating</span>

                <strong>
                  {{
                    Number(
                      selectedFeedback.feed_rating
                    ).toFixed(1)
                  }}
                  / 5
                </strong>
              </div>

              <div class="detail-item">
                <span>Parent Type</span>

                <strong>
                  {{
                    selectedFeedback.feed_parent_type ||
                    "—"
                  }}
                </strong>
              </div>

              <div class="detail-item">
                <span>Parent</span>

                <strong>
                  {{ getRelatedName(selectedFeedback) }}
                </strong>
              </div>

              <div class="detail-item">
                <span>Parent ID</span>

                <strong>
                  {{
                    selectedFeedback.feed_parent ??
                    "—"
                  }}
                </strong>
              </div>

              <div class="detail-item">
                <span>User ID</span>

                <strong>
                  {{
                    selectedFeedback.feed_by ??
                    "—"
                  }}
                </strong>
              </div>

              <div class="detail-item">
                <span>Created</span>

                <strong>
                  {{
                    formatDate(
                      selectedFeedback.feed_datetime
                    )
                  }}
                </strong>
              </div>

              <div class="detail-item">
                <span>Feedback ID</span>

                <strong>
                  #{{ selectedFeedback.feed_id }}
                </strong>
              </div>

            </div>

          </div>

          <!-- Media -->
          <div class="detail-section">

            <h4>Media</h4>

            <div class="media-preview">

              <div
                v-if="selectedFeedback.feed_img"
              >

                <span>Image</span>

                <img
                  :src="selectedFeedback.feed_img"
                  alt="Review"
                />

              </div>

              <a
                v-if="selectedFeedback.feed_video"
                :href="selectedFeedback.feed_video"
                target="_blank"
                rel="noopener noreferrer"
                class="video-link"
              >
                <i class="bx bx-play-circle"></i>
                View Video
              </a>

              <span
                v-if="
                  !selectedFeedback.feed_img &&
                  !selectedFeedback.feed_video
                "
                class="no-data"
              >
                No media attached.
              </span>

            </div>

          </div>

        </div>

        <div class="modal-footer">

          <button
            class="secondary-btn"
            @click="showViewModal = false"
          >
            Close
          </button>

          <button
            class="primary-btn"
            @click="
              showViewModal = false;
              editFeedback(selectedFeedback);
            "
          >
            <i class="bx bx-edit-alt"></i>
            Edit Review
          </button>

        </div>

      </div>

    </div>

    <!-- Delete Modal -->
    <div
      v-if="showDeleteModal"
      class="modal-overlay"
      @click.self="showDeleteModal = false"
    >

      <div class="delete-modal">

        <div class="delete-icon">
          <i class="bx bx-trash"></i>
        </div>

        <h2>Delete Review?</h2>

        <p>
          Are you sure you want to delete this review by
          <strong>
            {{ selectedFeedback?.user_name }}
          </strong>?

          This action cannot be undone.
        </p>

        <div class="delete-actions">

          <button
            class="secondary-btn"
            :disabled="deleting"
            @click="showDeleteModal = false"
          >
            Cancel
          </button>

          <button
            class="danger-btn"
            :disabled="deleting"
            @click="deleteFeedback"
          >

            <i
              class="bx"
              :class="
                deleting
                  ? 'bx-loader-alt bx-spin'
                  : 'bx-trash'
              "
            ></i>

            {{
              deleting
                ? "Deleting..."
                : "Delete Review"
            }}

          </button>

        </div>

      </div>

    </div>

  </div>
</template>
<script>
import {
  getFeedbacksApi,
  getFeedbackStatsApi,
  getFeedbackApi,
  createFeedbackApi,
  updateFeedbackApi,
  deleteFeedbackApi,
} from "@/apis/feedbackapis";

import { getCoursesApi } from "@/apis/courseapis";
import { getCreatorsApi } from "@/apis/creatorapis";
import { getUsersApi } from "@/apis/usersapis";
import { getBlogsApi } from "@/apis/blogapis";

export default {
  name: "FeedbacksScreen",

  data() {
    return {
      // =====================================================
      // FILTERS
      // =====================================================

      search: "",
      ratingFilter: "",
      typeFilter: "",
      mediaFilter: "",

      // =====================================================
      // PAGINATION
      // =====================================================

      currentPage: 1,
      perPage: 8,
      totalFeedbacks: 0,
      totalPagesCount: 1,

      // =====================================================
      // DATA
      // =====================================================

      feedbacks: [],

      users: [],
      courses: [],
      creators: [],
      blogs: [],

      // =====================================================
      // STATS
      // =====================================================

      stats: {
        totalReviews: 0,
        averageRating: "0.0",
        videoReviews: 0,
        courseReviews: 0,
        creatorReviews: 0,

        fiveStar: 0,
        fourStar: 0,
        threeStar: 0,
        twoStar: 0,
        oneStar: 0,
        zeroStar: 0,

        homeReviews: 0,
        blogReviews: 0,
      },

      // =====================================================
      // LOADING
      // =====================================================

      loading: false,
      saving: false,
      deleting: false,

      // =====================================================
      // MODALS
      // =====================================================

      showFormModal: false,
      showViewModal: false,
      showDeleteModal: false,

      isEditing: false,
      selectedFeedback: null,

      // =====================================================
      // FORM
      // =====================================================

      form: {
        feed_id: null,
        feed_parent: null,
        feed_parent_type: "",
        feed_video: "",
        feed_img: "",
        feed_text: "",
        feed_meta_text: "",
        feed_keyword: "",
        feed_rating: 5,
        feed_by: null,
      },
    };
  },

  // =======================================================
  // COMPUTED
  // =======================================================

  computed: {
    // -----------------------------------------------------
    // CURRENT PAGE RANGE
    // -----------------------------------------------------

    startItem() {
      if (!this.totalFeedbacks) {
        return 0;
      }

      return (
        (this.currentPage - 1) *
          this.perPage +
        1
      );
    },

    endItem() {
      return Math.min(
        this.currentPage * this.perPage,
        this.totalFeedbacks
      );
    },

    // -----------------------------------------------------
    // RATING COUNTS
    // -----------------------------------------------------

    fiveStarCount() {
      return Number(this.stats.fiveStar || 0);
    },

    fourStarCount() {
      return Number(this.stats.fourStar || 0);
    },

    threeStarCount() {
      return Number(this.stats.threeStar || 0);
    },

    twoStarCount() {
      return Number(this.stats.twoStar || 0);
    },

    oneStarCount() {
      return Number(this.stats.oneStar || 0);
    },

    zeroStarCount() {
      return Number(this.stats.zeroStar || 0);
    },

    // -----------------------------------------------------
    // STATS
    // -----------------------------------------------------

    averageRating() {
      return this.stats.averageRating || "0.0";
    },

    videoReviews() {
      return Number(
        this.stats.videoReviews || 0
      );
    },

    courseReviews() {
      return Number(
        this.stats.courseReviews || 0
      );
    },
  },

  // =======================================================
  // WATCH
  // =======================================================

  watch: {
    search() {
      this.currentPage = 1;
      this.loadFeedbacks();
    },

    ratingFilter() {
      this.currentPage = 1;
      this.loadFeedbacks();
    },

    typeFilter() {
      this.currentPage = 1;
      this.loadFeedbacks();
    },

    mediaFilter() {
      this.currentPage = 1;
      this.loadFeedbacks();
    },

    "form.feed_parent_type"(value) {
      if (value === "Home") {
        this.form.feed_parent = 0;
      } else {
        this.form.feed_parent = null;
      }
    },
  },

  // =======================================================
  // MOUNTED
  // =======================================================

  mounted() {
    this.loadFeedbacks();
    this.loadStats();

    this.loadUsers();
    this.loadCourses();
    this.loadCreators();
    this.loadBlogs();
  },

  // =======================================================
  // METHODS
  // =======================================================

  methods: {
    // =====================================================
    // LOAD FEEDBACKS
    // =====================================================

    async loadFeedbacks() {
      this.loading = true;

      try {
        const res = await getFeedbacksApi({
          page: this.currentPage,
          limit: this.perPage,
          search: this.search,
          rating: this.ratingFilter,
          type: this.typeFilter,
          media: this.mediaFilter,
        });

        if (
          res &&
          res.status !== false
        ) {
          this.feedbacks =
            res.feedbacks || [];

          this.totalFeedbacks = Number(
            res.total || 0
          );

          this.totalPagesCount = Math.max(
            Number(res.totalPages || 1),
            1
          );

          this.currentPage = Number(
            res.page || this.currentPage
          );

          /*
           * If current page became invalid
           * after delete/filter.
           */
          if (
            this.currentPage >
              this.totalPagesCount &&
            this.totalPagesCount > 0
          ) {
            this.currentPage =
              this.totalPagesCount;

            await this.loadFeedbacks();
          }
        } else {
          this.feedbacks = [];
          this.totalFeedbacks = 0;
          this.totalPagesCount = 1;
        }
      } catch (error) {
        console.error(
          "Load Feedbacks Error:",
          error
        );

        this.feedbacks = [];
        this.totalFeedbacks = 0;
        this.totalPagesCount = 1;
      } finally {
        this.loading = false;
      }
    },

    // =====================================================
    // LOAD STATS
    // =====================================================

    async loadStats() {
      try {
        const res =
          await getFeedbackStatsApi();

        if (
          res &&
          res.status !== false &&
          res.stats
        ) {
          this.stats = {
            ...this.stats,
            ...res.stats,
          };
        }
      } catch (error) {
        console.error(
          "Load Feedback Stats Error:",
          error
        );
      }
    },

    // =====================================================
    // LOAD USERS
    // =====================================================

    async loadUsers() {
      try {
        const res = await getUsersApi({
          page: 1,
          limit: 100,
          search: "",
        });

        if (
          res &&
          res.status !== false
        ) {
          this.users = res.users || [];
        } else {
          this.users = [];
        }
      } catch (error) {
        console.error(
          "Load Users Error:",
          error
        );

        this.users = [];
      }
    },

    // =====================================================
    // LOAD COURSES
    // =====================================================

    async loadCourses() {
      try {
        const res = await getCoursesApi({
          page: 1,
          limit: 100,
          search: "",
          course_typ: "",
          category: "",
          creator_id: "",
        });

        if (
          res &&
          res.status !== false
        ) {
          this.courses =
            res.courses || [];
        } else {
          this.courses = [];
        }
      } catch (error) {
        console.error(
          "Load Courses Error:",
          error
        );

        this.courses = [];
      }
    },

    // =====================================================
    // LOAD CREATORS
    // =====================================================

    async loadCreators() {
      try {
        const res = await getCreatorsApi({
          page: 1,
          limit: 100,
          search: "",
          designation: "",
          business: "",
        });

        if (
          res &&
          res.status !== false
        ) {
          this.creators =
            res.creators || [];
        } else {
          this.creators = [];
        }
      } catch (error) {
        console.error(
          "Load Creators Error:",
          error
        );

        this.creators = [];
      }
    },

    // =====================================================
    // LOAD BLOGS
    // =====================================================

    async loadBlogs() {
      try {
        const res = await getBlogsApi({
          page: 1,
          limit: 100,
          search: "",
        });

        if (
          res &&
          res.status !== false
        ) {
          this.blogs =
            res.blogs || [];
        } else {
          this.blogs = [];
        }
      } catch (error) {
        console.error(
          "Load Blogs Error:",
          error
        );

        this.blogs = [];
      }
    },

    // =====================================================
    // USER NAME
    // =====================================================

    getUserName(userId) {
      if (
        userId === null ||
        userId === undefined ||
        userId === ""
      ) {
        return "—";
      }

      const user = this.users.find(
        (item) =>
          Number(item.user_id) ===
          Number(userId)
      );

      return user
        ? (
            user.name ||
            user.email ||
            `User #${user.user_id}`
          )
        : `User #${userId}`;
    },

    // =====================================================
    // COURSE NAME
    // =====================================================

    getCourseName(courseId) {
      if (
        courseId === null ||
        courseId === undefined ||
        courseId === ""
      ) {
        return "—";
      }

      const course = this.courses.find(
        (item) =>
          Number(item.course_id) ===
          Number(courseId)
      );

      return course
        ? course.title
        : `Course #${courseId}`;
    },

    // =====================================================
    // CREATOR NAME
    // =====================================================

    getCreatorName(creatorId) {
      if (
        creatorId === null ||
        creatorId === undefined ||
        creatorId === ""
      ) {
        return "—";
      }

      const creator =
        this.creators.find(
          (item) =>
            Number(item.creator_id) ===
            Number(creatorId)
        );

      return creator
        ? (
            creator.cre_username ||
            creator.cre_buss_name ||
            `Creator #${creator.creator_id}`
          )
        : `Creator #${creatorId}`;
    },

    // =====================================================
    // BLOG NAME
    // =====================================================

    getBlogName(blogId) {
      if (
        blogId === null ||
        blogId === undefined ||
        blogId === ""
      ) {
        return "—";
      }

      const blog = this.blogs.find(
        (item) =>
          Number(item.blog_id) ===
          Number(blogId)
      );

      return blog
        ? (
            blog.blog_title ||
            blog.title ||
            `Blog #${blog.blog_id}`
          )
        : `Blog #${blogId}`;
    },

    // =====================================================
    // RELATED NAME
    // =====================================================

    getRelatedName(feedback) {
      if (!feedback) {
        return "—";
      }

      const type =
        feedback.feed_parent_type;

      if (type === "Home") {
        return "Home";
      }

      if (type === "Course") {
        return this.getCourseName(
          feedback.feed_parent
        );
      }

      if (type === "Creator") {
        return this.getCreatorName(
          feedback.feed_parent
        );
      }

      if (type === "Blog") {
        return this.getBlogName(
          feedback.feed_parent
        );
      }

      return "—";
    },

    // =====================================================
    // INITIALS
    // =====================================================

    getInitials(name) {
      if (!name) {
        return "U";
      }

      return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((word) =>
          word
            .charAt(0)
            .toUpperCase()
        )
        .join("");
    },

    // =====================================================
    // DATE
    // =====================================================

    formatDate(date) {
      if (!date) {
        return "—";
      }

      const value = new Date(date);

      if (
        Number.isNaN(value.getTime())
      ) {
        return date;
      }

      return value.toLocaleDateString(
        "en-IN",
        {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }
      );
    },

    // =====================================================
    // RATING COUNT
    // =====================================================

    ratingCount(rating) {
      switch (Number(rating)) {
        case 5:
          return this.fiveStarCount;

        case 4:
          return this.fourStarCount;

        case 3:
          return this.threeStarCount;

        case 2:
          return this.twoStarCount;

        case 1:
          return this.oneStarCount;

        case 0:
          return this.zeroStarCount;

        default:
          return 0;
      }
    },

    // =====================================================
    // RATING PERCENTAGE
    // =====================================================

    ratingPercentage(rating) {
      if (!this.totalFeedbacks) {
        return 0;
      }

      return (
        (this.ratingCount(rating) /
          this.totalFeedbacks) *
        100
      );
    },

    // =====================================================
    // RESET FILTERS
    // =====================================================

    resetFilters() {
      this.search = "";
      this.ratingFilter = "";
      this.typeFilter = "";
      this.mediaFilter = "";
      this.currentPage = 1;

      this.$nextTick(() => {
        this.loadFeedbacks();
      });
    },

    // =====================================================
    // EMPTY FORM
    // =====================================================

    getEmptyForm() {
      return {
        feed_id: null,
        feed_parent: null,
        feed_parent_type: "",
        feed_video: "",
        feed_img: "",
        feed_text: "",
        feed_meta_text: "",
        feed_keyword: "",
        feed_rating: 5,
        feed_by: null,
      };
    },

    // =====================================================
    // OPEN ADD MODAL
    // =====================================================

    openAddModal() {
      this.isEditing = false;
      this.selectedFeedback = null;

      this.form =
        this.getEmptyForm();

      this.showFormModal = true;
    },

    // =====================================================
    // CLOSE FORM MODAL
    // =====================================================

    closeFormModal() {
      if (this.saving) {
        return;
      }

      this.showFormModal = false;
      this.isEditing = false;

      this.form =
        this.getEmptyForm();
    },

    // =====================================================
    // VIEW FEEDBACK
    // =====================================================

    async viewFeedback(feedback) {
      if (!feedback?.feed_id) {
        return;
      }

      try {
        const res =
          await getFeedbackApi(
            feedback.feed_id
          );

        if (
          res &&
          res.status !== false &&
          res.feedback
        ) {
          this.selectedFeedback =
            res.feedback;
        } else {
          this.selectedFeedback =
            feedback;
        }

        this.showViewModal = true;
      } catch (error) {
        console.error(
          "View Feedback Error:",
          error
        );

        this.selectedFeedback =
          feedback;

        this.showViewModal = true;
      }
    },

    // =====================================================
    // EDIT FEEDBACK
    // =====================================================

    async editFeedback(feedback) {
      if (!feedback?.feed_id) {
        return;
      }

      try {
        const res =
          await getFeedbackApi(
            feedback.feed_id
          );

        const data =
          res &&
          res.status !== false &&
          res.feedback
            ? res.feedback
            : feedback;

        this.selectedFeedback =
          data;

        this.isEditing = true;

        this.form = {
          ...this.getEmptyForm(),

          feed_id:
            data.feed_id,

          feed_parent:
            data.feed_parent,

          feed_parent_type:
            data.feed_parent_type || "",

          feed_video:
            data.feed_video || "",

          feed_img:
            data.feed_img || "",

          feed_text:
            data.feed_text || "",

          feed_meta_text:
            data.feed_meta_text || "",

          feed_keyword:
            data.feed_keyword || "",

          feed_rating:
            Number(
              data.feed_rating || 5
            ),

          feed_by:
            data.feed_by,

        };

        if (
          this.form.feed_parent_type ===
          "Home"
        ) {
          this.form.feed_parent = 0;
        }

        this.showViewModal = false;
        this.showFormModal = true;
      } catch (error) {
        console.error(
          "Edit Feedback Error:",
          error
        );

        this.selectedFeedback =
          feedback;

        this.isEditing = true;

        this.form = {
          ...this.getEmptyForm(),

          feed_id:
            feedback.feed_id,

          feed_parent:
            feedback.feed_parent,

          feed_parent_type:
            feedback.feed_parent_type || "",

          feed_video:
            feedback.feed_video || "",

          feed_img:
            feedback.feed_img || "",

          feed_text:
            feedback.feed_text || "",

          feed_meta_text:
            feedback.feed_meta_text || "",

          feed_keyword:
            feedback.feed_keyword || "",

          feed_rating:
            Number(
              feedback.feed_rating || 5
            ),

          feed_by:
            feedback.feed_by,

        };

        if (
          this.form.feed_parent_type ===
          "Home"
        ) {
          this.form.feed_parent = 0;
        }

        this.showViewModal = false;
        this.showFormModal = true;
      }
    },

    // =====================================================
    // SAVE FEEDBACK
    // =====================================================

    async saveFeedback() {
      if (
        !this.form.feed_by
      ) {
        alert(
          "Please select a user."
        );

        return;
      }

      if (
        !this.form.feed_parent_type
      ) {
        alert(
          "Please select parent type."
        );

        return;
      }

      if (
        this.form.feed_parent_type !==
          "Home" &&
        !this.form.feed_parent
      ) {
        alert(
          `Please select ${this.form.feed_parent_type}.`
        );

        return;
      }

      if (
        !this.form.feed_text ||
        !this.form.feed_text.trim()
      ) {
        alert(
          "Please enter review text."
        );

        return;
      }

      const rating = Number(
        this.form.feed_rating
      );

      if (
        !rating ||
        rating < 1 ||
        rating > 5
      ) {
        alert(
          "Please select a valid rating."
        );

        return;
      }

      if (
        this.form.feed_parent_type ===
        "Home"
      ) {
        this.form.feed_parent = 0;
      }

      this.saving = true;

      try {
        let res;

        if (this.isEditing) {
          if (
            !this.form.feed_id
          ) {
            alert(
              "Feedback ID is missing."
            );

            return;
          }

          res =
            await updateFeedbackApi(
              this.form
            );
        } else {
          res =
            await createFeedbackApi(
              this.form
            );
        }

        if (
          res &&
          res.status !== false
        ) {
          alert(
            res.success ||
              (
                this.isEditing
                  ? "Review updated successfully."
                  : "Review created successfully."
              )
          );

          this.showFormModal = false;
          this.isEditing = false;
          this.selectedFeedback = null;

          this.form =
            this.getEmptyForm();

          this.currentPage = 1;

          await this.loadFeedbacks();
          await this.loadStats();
        } else {
          alert(
            res?.success ||
              "Unable to save review."
          );
        }
      } catch (error) {
        console.error(
          "Save Feedback Error:",
          error
        );

        alert(
          error?.response?.data?.success ||
            "Something went wrong while saving review."
        );
      } finally {
        this.saving = false;
      }
    },

    // =====================================================
    // CONFIRM DELETE
    // =====================================================

    confirmDelete(feedback) {
      if (!feedback?.feed_id) {
        return;
      }

      this.selectedFeedback =
        feedback;

      this.showDeleteModal = true;
    },

    // =====================================================
    // DELETE FEEDBACK
    // =====================================================

    async deleteFeedback() {
      if (
        !this.selectedFeedback?.feed_id
      ) {
        return;
      }

      this.deleting = true;

      try {
        const res =
          await deleteFeedbackApi(
            this.selectedFeedback.feed_id
          );

        if (
          res &&
          res.status !== false
        ) {
          alert(
            res.success ||
              "Review deleted successfully."
          );

          this.showDeleteModal = false;
          this.selectedFeedback = null;

          /*
           * If deleting the only record
           * on the current page, move back.
           */
          if (
            this.feedbacks.length === 1 &&
            this.currentPage > 1
          ) {
            this.currentPage--;
          }

          await this.loadFeedbacks();
          await this.loadStats();
        } else {
          alert(
            res?.success ||
              "Review deletion failed."
          );
        }
      } catch (error) {
        console.error(
          "Delete Feedback Error:",
          error
        );

        alert(
          error?.response?.data?.success ||
            "Something went wrong while deleting review."
        );
      } finally {
        this.deleting = false;
      }
    },

    // =====================================================
    // PAGINATION
    // =====================================================

    goToPage(page) {
      if (
        page < 1 ||
        page > this.totalPagesCount ||
        page === this.currentPage
      ) {
        return;
      }

      this.currentPage = page;

      this.loadFeedbacks();
    },

    previousPage() {
      if (this.currentPage <= 1) {
        return;
      }

      this.currentPage--;

      this.loadFeedbacks();
    },

    nextPage() {
      if (
        this.currentPage >=
        this.totalPagesCount
      ) {
        return;
      }

      this.currentPage++;

      this.loadFeedbacks();
    },
  },
};
</script>
<style scoped>
.feedback-wrapper {
  padding: 24px;
  background: #f8fafc;
  min-height: calc(100vh - 70px);
  color: #0f172a;
}

/* Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 22px;
}

.page-header h1 {
  margin: 0 0 5px;
  font-size: 25px;
  font-weight: 700;
}

.page-header p {
  margin: 0;
  color: #64748b;
  font-size: 14px;
}

/* Buttons */
.primary-btn,
.secondary-btn,
.reset-btn,
.danger-btn {
  height: 40px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  transition: 0.2s;
}

.primary-btn {
  background: #2563eb;
  color: white;
}

.primary-btn:hover {
  background: #1d4ed8;
}

.secondary-btn {
  background: white;
  color: #334155;
  border-color: #e2e8f0;
}

.secondary-btn:hover {
  background: #f8fafc;
}

.reset-btn {
  background: white;
  color: #475569;
  border-color: #e2e8f0;
}

.reset-btn:hover {
  background: #f8fafc;
}

.danger-btn {
  background: #dc2626;
  color: white;
}

.danger-btn:hover {
  background: #b91c1c;
}

/* Stats */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

.stat-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 17px;
  display: flex;
  align-items: center;
  gap: 13px;
}

.stat-icon {
  width: 43px;
  height: 43px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 21px;
}

.stat-icon.blue {
  background: #eff6ff;
  color: #2563eb;
}

.stat-icon.green {
  background: #f0fdf4;
  color: #16a34a;
}

.stat-icon.orange {
  background: #fff7ed;
  color: #ea580c;
}

.stat-icon.purple {
  background: #faf5ff;
  color: #9333ea;
}

.stat-card span {
  display: block;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
}

.stat-card strong {
  font-size: 21px;
}

/* Rating Summary */
.rating-summary-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  margin-bottom: 20px;
  padding: 20px 24px;
  display: flex;
  align-items: center;
  gap: 55px;
}

.rating-overall {
  width: 170px;
  text-align: center;
  flex-shrink: 0;
}

.rating-overall > strong {
  display: block;
  font-size: 35px;
  line-height: 1;
  margin-bottom: 9px;
}

.rating-overall > span {
  display: block;
  margin-top: 7px;
  color: #94a3b8;
  font-size: 11px;
}

.stars {
  display: flex;
  align-items: center;
  gap: 2px;
}

.stars.large {
  justify-content: center;
}

.stars i {
  color: #f59e0b;
  font-size: 14px;
}

.stars.large i {
  font-size: 19px;
}

.rating-bars {
  width: 430px;
  max-width: 100%;
}

.rating-row {
  display: flex;
  align-items: center;
  gap: 7px;
  height: 25px;
}

.rating-row > span {
  width: 10px;
  font-size: 12px;
  color: #475569;
  text-align: right;
}

.rating-row > i {
  color: #f59e0b;
  font-size: 13px;
}

.rating-progress {
  flex: 1;
  height: 6px;
  background: #f1f5f9;
  border-radius: 10px;
  overflow: hidden;
}

.rating-progress-fill {
  height: 100%;
  background: #f59e0b;
  border-radius: 10px;
}

.rating-row small {
  width: 20px;
  color: #94a3b8;
  font-size: 11px;
}

/* Card */
.content-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

/* Toolbar */
.toolbar {
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid #e2e8f0;
}

.search-box {
  position: relative;
  flex: 1;
  max-width: 360px;
}

.search-box i {
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 18px;
}

.search-box input,
.filter-select {
  height: 40px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  outline: none;
  font-size: 13px;
  color: #334155;
  background: white;
}

.search-box input {
  width: 100%;
  padding: 0 13px 0 39px;
}

.filter-select {
  min-width: 135px;
  padding: 0 12px;
}

.search-box input:focus,
.filter-select:focus,
.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  border-color: #93c5fd;
}

/* Table */
.table-wrapper {
  overflow-x: auto;
}

table {
  width: 100%;
  min-width: 1200px;
  border-collapse: collapse;
}

th {
  background: #f8fafc;
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  text-align: left;
  padding: 13px 16px;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

td {
  padding: 13px 16px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
  font-size: 13px;
}

tbody tr:hover {
  background: #fafcff;
}

.reviewer-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 12px;
}

.avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.reviewer-info strong {
  display: block;
  font-size: 12px;
  margin-bottom: 3px;
}

.reviewer-info small,
.related-info small {
  display: block;
  color: #94a3b8;
  font-size: 10px;
}

.review-text {
  max-width: 280px;
}

.review-text strong {
  display: block;
  color: #334155;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.review-text small {
  display: block;
  color: #94a3b8;
  font-size: 10px;
  margin-top: 3px;
}

.rating-cell {
  display: flex;
  align-items: center;
  gap: 7px;
}

.rating-cell > span {
  color: #475569;
  font-size: 12px;
  font-weight: 600;
}

.type-badge {
  display: inline-flex;
  padding: 5px 9px;
  background: #eff6ff;
  color: #2563eb;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 600;
}

.related-info strong {
  display: block;
  max-width: 150px;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.media-list {
  display: flex;
  gap: 5px;
}

.media-list span {
  width: 27px;
  height: 27px;
  border-radius: 6px;
  background: #f1f5f9;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
}

.media-list .no-media {
  width: auto;
  padding: 0 7px;
  font-size: 10px;
}

.date-text {
  color: #64748b;
  font-size: 11px;
  white-space: nowrap;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 5px;
}

.icon-btn {
  width: 32px;
  height: 32px;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
}

.icon-btn.view {
  color: #64748b;
}

.icon-btn.view:hover {
  background: #f1f5f9;
}

.icon-btn.edit {
  color: #2563eb;
}

.icon-btn.edit:hover {
  background: #eff6ff;
}

.icon-btn.delete {
  color: #dc2626;
}

.icon-btn.delete:hover {
  background: #fef2f2;
}

/* Empty */
.empty-state {
  text-align: center;
  padding: 60px 20px !important;
  color: #94a3b8;
}

.empty-state i {
  font-size: 42px;
}

.empty-state h3 {
  margin: 10px 0 4px;
  color: #334155;
  font-size: 15px;
}

.empty-state p {
  margin: 0;
  font-size: 13px;
}

/* Pagination */
.pagination {
  padding: 14px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #64748b;
  font-size: 12px;
}

.pagination-buttons {
  display: flex;
  gap: 5px;
}

.pagination-buttons button {
  min-width: 31px;
  height: 31px;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  color: #475569;
}

.pagination-buttons button.active {
  background: #2563eb;
  border-color: #2563eb;
  color: white;
}

.pagination-buttons button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.48);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.modal {
  width: 760px;
  max-width: 100%;
  max-height: 90vh;
  background: white;
  border-radius: 14px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.view-modal {
  width: 700px;
}

.modal-header {
  padding: 20px 22px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.modal-header h2 {
  margin: 0 0 4px;
  font-size: 19px;
}

.modal-header p {
  margin: 0;
  color: #64748b;
  font-size: 12px;
}

.close-btn {
  width: 34px;
  height: 34px;
  border: none;
  background: #f8fafc;
  border-radius: 7px;
  font-size: 20px;
  color: #64748b;
  cursor: pointer;
}

.modal-body {
  padding: 22px;
  overflow-y: auto;
}

.modal-footer {
  padding: 15px 22px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 9px;
}

/* Form */
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.form-group.full {
  grid-column: 1 / -1;
}

.form-group label {
  font-size: 12px;
  font-weight: 600;
  color: #334155;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 11px;
  outline: none;
  font-size: 13px;
  color: #334155;
  font-family: inherit;
}

.form-group input,
.form-group select {
  height: 40px;
}

.form-group textarea {
  resize: vertical;
}

/* View */
.view-reviewer {
  display: flex;
  align-items: center;
  gap: 14px;
  padding-bottom: 20px;
  margin-bottom: 20px;
  border-bottom: 1px solid #e2e8f0;
}

.large-avatar {
  width: 65px;
  height: 65px;
  flex-shrink: 0;
  border-radius: 50%;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  font-size: 19px;
  font-weight: 700;
}

.large-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.view-reviewer h3 {
  margin: 0 0 3px;
  font-size: 18px;
}

.view-reviewer p {
  margin: 0 0 5px;
  color: #94a3b8;
  font-size: 12px;
}

.detail-section {
  margin-bottom: 22px;
}

.detail-section h4 {
  margin: 0 0 12px;
  font-size: 13px;
  color: #334155;
}

.review-detail-box {
  padding: 15px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 9px;
}

.review-detail-box p {
  margin: 0;
  color: #334155;
  font-size: 13px;
  line-height: 1.7;
}

.review-detail-box small {
  display: block;
  margin-top: 8px;
  color: #94a3b8;
  font-size: 11px;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.detail-item {
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.detail-item span {
  display: block;
  color: #94a3b8;
  font-size: 11px;
  margin-bottom: 5px;
}

.detail-item strong {
  color: #334155;
  font-size: 12px;
}

.media-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.media-preview > div {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.media-preview > div > span {
  color: #64748b;
  font-size: 11px;
}

.media-preview img {
  width: 120px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.video-link {
  height: 38px;
  padding: 0 12px;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #2563eb;
  text-decoration: none;
  font-size: 12px;
}

.no-data {
  color: #94a3b8;
  font-size: 12px;
}

/* Delete */
.delete-modal {
  width: 400px;
  max-width: 100%;
  background: white;
  border-radius: 14px;
  padding: 28px;
  text-align: center;
}

.delete-icon {
  width: 54px;
  height: 54px;
  margin: 0 auto 15px;
  border-radius: 50%;
  background: #fef2f2;
  color: #dc2626;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.delete-modal h2 {
  margin: 0 0 8px;
  font-size: 19px;
}

.delete-modal p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.6;
}

.delete-actions {
  margin-top: 22px;
  display: flex;
  justify-content: center;
  gap: 9px;
}

/* Responsive */
@media (max-width: 1100px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .rating-summary-card {
    gap: 25px;
  }

  .toolbar {
    flex-wrap: wrap;
  }

  .search-box {
    max-width: none;
    flex-basis: 100%;
  }
}

@media (max-width: 700px) {
  .feedback-wrapper {
    padding: 16px;
  }

  .page-header {
    align-items: flex-start;
    gap: 15px;
  }

  .page-header h1 {
    font-size: 21px;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .rating-summary-card {
    flex-direction: column;
    align-items: stretch;
    gap: 20px;
  }

  .rating-overall {
    width: auto;
  }

  .toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .search-box {
    width: 100%;
  }

  .filter-select,
  .reset-btn {
    width: 100%;
  }

  .form-grid,
  .details-grid {
    grid-template-columns: 1fr;
  }

  .form-group.full {
    grid-column: auto;
  }

  .pagination {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
}
</style>